/**
 * DashGraph - React Component for Dash
 * 
 * This component wraps the MerlinGraph (GraphExplorer with UI modules) 
 * for use in Dash applications.
 */

import React, { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import { MerlinGraph } from '../../index.js';
import './DashGraph.css';

class DashGraph extends Component {
    constructor(props) {
        super(props);
        this.containerRef = createRef();
        this.graph = null;
        this._mounted = false;
        this._graphReady = false;
        this._pendingPropSync = false;
        // (removed _initialSearchDone — all searches now wait for completion)
    }

    componentDidMount() {
        this._mounted = true;
        this._initGraph();
    }

    componentDidUpdate(prevProps) {
        if (!this.graph) {
            return;
        }

        if (!this._graphReady) {
            this._pendingPropSync = true;
            return;
        }

        this._syncGraphProps(prevProps);
    }

    _syncGraphProps(prevProps = {}) {
        // Handle workspace_id updates - full workspace switch with data reload.
        // Skip if cursor_id also changed — the cursor handler below will set
        // workspace_id directly and execute the query / load the cursor without
        // the redundant resetCursor + fetchAndUpdate that setWorkspace does.
        const workspaceChanged = prevProps.workspace_id !== this.props.workspace_id && this.graph && this.props.workspace_id;
        const cursorAlsoChanged = prevProps.cursor_id !== this.props.cursor_id && this.props.cursor_id;
        if (workspaceChanged && !cursorAlsoChanged) {
            console.log('[DashGraph] Workspace changed:', prevProps.workspace_id, '->', this.props.workspace_id);
            if (typeof this.graph.setWorkspace === 'function') {
                this.graph.setWorkspace(this.props.workspace_id)
                    .catch(err => console.error('[DashGraph] Workspace switch failed:', err));
            } else {
                this.graph.options.workspace_id = this.props.workspace_id;
            }
        } else if (workspaceChanged && cursorAlsoChanged) {
            // Just update workspace_id; the cursor handler will do the rest.
            console.log('[DashGraph] Workspace + cursor changed together:', this.props.workspace_id, this.props.cursor_id);
            this.graph.options.workspace_id = this.props.workspace_id;
            if (this.graph.explorer && this.graph.explorer.options) {
                this.graph.explorer.options.workspace_id = this.props.workspace_id;
                this.graph.explorer.options.workspaceId = this.props.workspace_id;
            }
            if (window.ExplorerAPI && window.ExplorerAPI.setConfig) {
                window.ExplorerAPI.setConfig({ workspaceId: this.props.workspace_id });
            }
        }
        
        // Handle nodes updates
        if (prevProps.nodes !== this.props.nodes && this.graph) {
            this.graph.setNodes(this.props.nodes || []);
        }
        
        // Handle edges updates
        if (prevProps.edges !== this.props.edges && this.graph) {
            this.graph.setEdges(this.props.edges || []);
        }
        
        // Handle search_text updates — delegates to ExplorerAPI.searchAsk
        // This is the primary search flow: creates A2A task, refreshes graph, opens task card
        // Check _search_nonce too so repeated identical searches still trigger
        const searchTriggered = this.graph && this.props.search_text && this.props.workspace_id && (
            prevProps.search_text !== this.props.search_text ||
            prevProps._search_nonce !== this.props._search_nonce
        );
        if (searchTriggered) {
            console.log('[DashGraph] Executing search:', this.props.search_text);
            this.graph.options.workspace_id = this.props.workspace_id;
            // Always wait for the agent task to complete before refreshing
            // the graph — keeps the loading wheel visible until data is ready.
            // The chat panel streams the agent response in parallel.
            const searchOpts = {
                resetCursor: !this.props.create_cursor,  // Only reset if not creating a new cursor
                createCursor: this.props.create_cursor || false,
                waitForCompletion: true,
            };
            if (this.props.options && this.props.options.agent_id) {
                searchOpts.agent_id = this.props.options.agent_id;
            }
            this.graph.searchAsk(
                this.props.search_text,
                this.props.search_keywords || [],
                searchOpts
            ).catch(err => console.error('[DashGraph] searchAsk failed:', err));
        }
        // Handle cursor_id updates - only load if we have both cursor_id and workspace_id
        // Skip if search_text also changed (searchAsk handles graph refresh internally)
        else if (prevProps.cursor_id !== this.props.cursor_id && this.graph && this.props.cursor_id && this.props.workspace_id) {
            this.graph.options.workspace_id = this.props.workspace_id;

            const queryAlsoChanged = prevProps.query !== this.props.query && this.props.query;

            if (queryAlsoChanged) {
                // Both cursor_id AND query changed in the same Dash update.
                // Reset the cursor first so previous query results are cleared,
                // then execute the new query and refresh.
                console.log('[DashGraph] Loading cursor with query:', this.props.cursor_id, 'query:', this.props.query.substring(0, 80));
                this.graph.options.cursor_id = this.props.cursor_id;
                // Sync ExplorerAPI so refreshGraph uses the correct cursor_id
                if (window.ExplorerAPI && window.ExplorerAPI.setConfig) {
                    window.ExplorerAPI.setConfig({ cursorId: this.props.cursor_id });
                }
                // Show loading wheel before the async requests so the empty-state
                // "No nodes in graph" overlay is replaced with the spinner.
                if (this.graph.explorer && typeof this.graph.explorer._showLoading === 'function') {
                    this.graph.explorer._showLoading(null, { sequential: true });
                }
                // Reset cursor before executing the new query to clear stale data
                const resetFirst = (!this.props.cursor_id || this.props.cursor_id === 'me')
                    ? this.graph.resetCursor().catch(e => console.warn('[DashGraph] Pre-query cursor reset warning:', e))
                    : Promise.resolve();
                resetFirst
                    .then(() => this.graph.executeQuery(this.props.query))
                    .then(() => this.graph.fetchAndUpdate())
                    .catch(err => console.error('[DashGraph] Query + fetch failed:', err));
            } else {
                // Only cursor_id changed - load cursor normally (fetches current data)
                console.log('[DashGraph] Loading cursor:', this.props.cursor_id, 'workspace:', this.props.workspace_id);
                this.graph.loadCursor(this.props.cursor_id);
            }
        }
        
        // Handle query-only updates (FQL queries only, not natural language)
        // Skip if cursor_id also changed — handled above
        else if (prevProps.query !== this.props.query && this.graph && this.props.query) {
            // Reset cursor before running new query so stale nodes are cleared
            const cursorId = this.graph.options.cursor_id;
            const resetFirst = (!cursorId || cursorId === 'me')
                ? this.graph.resetCursor().catch(e => console.warn('[DashGraph] Pre-query cursor reset warning:', e))
                : Promise.resolve();
            resetFirst
                .then(() => this.graph.executeQuery(this.props.query))
                .then(() => this.graph.fetchAndUpdate())
                .catch(err => console.error('[DashGraph] Query execution failed:', err));
        }
        
        // Handle theme updates
        if (prevProps.options?.theme !== this.props.options?.theme && this.graph) {
            this.graph.setTheme(this.props.options?.theme || 'dark');
        }
    }

    componentWillUnmount() {
        this._mounted = false;
        this._graphReady = false;
        this._pendingPropSync = false;
        if (this.graph) {
            this.graph.destroy();
            this.graph = null;
        }
    }

    async _initGraph() {
        if (!this.containerRef.current || !this._mounted) return;
        
        const { 
            options = {}, 
            cursor_id,
            workspace_id,
            query,
            search_text,
            search_keywords,
            _search_nonce,
            nodes,
            edges,
            api_endpoint,
        } = this.props;

        const initialProps = {
            options,
            cursor_id,
            workspace_id,
            query,
            search_text,
            search_keywords,
            _search_nonce,
            nodes,
            edges,
        };
        let graphReady = false;
        
        try {
            // Initialize MerlinGraph with options
            this.graph = new MerlinGraph(this.containerRef.current, {
                theme: options.theme || 'dark',
                show_legend: options.show_legend !== false,
                show_fql: options.show_fql !== false,
                show_toolbar: options.show_toolbar !== false,
                show_timeline: options.show_timeline !== false,
                show_chat: options.show_chat !== false,
                node_color: options.node_color || '#4CAF50',
                physics_enabled: options.physics_enabled !== false,
                physics_solver: options.physics_solver || 'forceAtlas2Based',
                extension_mode: options.extension_mode || false,
                api_endpoint: api_endpoint,
                workspace_id: workspace_id,
                onNodeSelect: this._handleNodeSelect.bind(this),
                onEdgeSelect: this._handleEdgeSelect.bind(this),
            });

            // Wait for graph to be ready
            await this.graph.ready();
            graphReady = true;
            
            if (!this._mounted) return;

            // Handle search_text on initial mount — this covers the case where
            // the modal opens with search_text already set as a prop (e.g. from
            // the ask module's _open_modal callback). Without this,
            // componentDidUpdate would never see a change because there are no
            // "previous" props to compare against on the first render.
            //
            // IMPORTANT: Read from this.props (not the destructured values captured
            // before await) because React may have delivered updated props while
            // MerlinGraph was initialising (race between componentDidUpdate and
            // this async function).
            const latestSearchText = this.props.search_text || search_text;
            const latestSearchKeywords = this.props.search_keywords || search_keywords;
            const latestWorkspaceId = this.props.workspace_id || workspace_id;
            const latestOptions = this.props.options || options;

            // Apply the latest theme now that the graph is ready. This handles the
            // race condition where componentDidUpdate fires while _initGraph is still
            // awaiting ready(), skipping the theme update because this.graph was null.
            // It also ensures the correct theme is applied when initial props use
            // theme: null (so any value from the server callback triggers a change).
            const latestTheme = latestOptions.theme || 'dark';
            const initTheme = options.theme || 'dark';
            if (latestTheme !== initTheme) {
                console.log('[DashGraph] Applying updated theme after ready:', latestTheme);
                this.graph.setTheme(latestTheme);
            }
            if (latestSearchText && latestWorkspaceId) {
                console.log('[DashGraph] Executing initial search:', latestSearchText, 'keywords:', latestSearchKeywords?.length || 0);
                this.graph.options.workspace_id = latestWorkspaceId;
                this._initialSearchDone = true;
                const searchOpts = {
                    resetCursor: !this.props.create_cursor,  // Only reset if not creating a new cursor
                    createCursor: this.props.create_cursor || false,
                    waitForCompletion: true,
                };
                if (latestOptions && latestOptions.agent_id) {
                    searchOpts.agent_id = latestOptions.agent_id;
                }
                this.graph.searchAsk(
                    latestSearchText,
                    latestSearchKeywords || [],
                    searchOpts
                ).catch(err => console.error('[DashGraph] Initial searchAsk failed:', err));
            }
            // Only load initial data if we have the necessary IDs
            // If cursor_id/workspace_id are provided later via props update, componentDidUpdate will handle it
            else if (cursor_id && workspace_id) {
                if (query) {
                    // Both cursor and query provided at init — reset cursor first to clear
                    // stale data from previous sessions, then execute the query and refresh.
                    console.log('[DashGraph] Loading cursor with query (init):', cursor_id, 'query:', query.substring(0, 80));
                    this.graph.options.cursor_id = cursor_id;
                    // Sync ExplorerAPI so refreshGraph uses the correct cursor_id
                    if (window.ExplorerAPI && window.ExplorerAPI.setConfig) {
                        window.ExplorerAPI.setConfig({ cursorId: cursor_id });
                    }
                    if (this.graph.explorer && typeof this.graph.explorer._showLoading === 'function') {
                        this.graph.explorer._showLoading(null, { sequential: true });
                    }
                    // Reset cursor before executing the new query
                    if (!cursor_id || cursor_id === 'me') {
                        try { await this.graph.resetCursor(); } catch (e) { console.warn('[DashGraph] Pre-query cursor reset warning (init):', e); }
                    }
                    await this.graph.executeQuery(query);
                    await this.graph.fetchAndUpdate();
                } else {
                    console.log('[DashGraph] Loading initial cursor:', cursor_id);
                    await this.graph.loadCursor(cursor_id);
                }
            } else if (query && workspace_id) {
                console.log('[DashGraph] Executing initial query:', query);
                await this.graph.executeQuery(query);
            } else if (nodes?.length || edges?.length) {
                console.log('[DashGraph] Setting initial data:', nodes?.length, 'nodes,', edges?.length, 'edges');
                this.graph.setData(nodes || [], edges || []);
            } else {
                console.log('[DashGraph] Initialized without data - waiting for cursor_id/workspace_id via props');
            }
        } catch (error) {
            console.error('[DashGraph] Initialization error:', error);
        } finally {
            if (graphReady && this._mounted && this.graph) {
                this._graphReady = true;

                if (this._pendingPropSync) {
                    this._pendingPropSync = false;
                    console.log('[DashGraph] Replaying deferred prop sync after graph ready');
                    this._syncGraphProps(initialProps);
                }
            }
        }
    }

    _handleNodeSelect(nodeIds) {
        const { setProps } = this.props;
        if (setProps) {
            setProps({ selected_nodes: nodeIds });
        }
    }

    _handleEdgeSelect(edgeIds) {
        const { setProps } = this.props;
        if (setProps) {
            setProps({ selected_edges: edgeIds });
        }
    }

    render() {
        const { id, className, height, width } = this.props;
        
        const style = {
            height: height || '100%',
            width: width || '100%',
        };
        
        // Handle Dash pattern matching IDs (which are objects)
        const domId = typeof id === 'object' ? JSON.stringify(id) : id;
        
        return (
            <div 
                id={domId}
                ref={this.containerRef}
                className={`dash-graph-container ${className || ''}`}
                style={style}
            />
        );
    }
}

DashGraph.defaultProps = {
    height: '100%',
    width: '100%',
    options: {},
    nodes: [],
    edges: [],
    selected_nodes: [],
    selected_edges: [],
};

DashGraph.propTypes = {
    /**
     * The ID used to identify this component in Dash callbacks.
     * Can be a string or an object for pattern matching.
     */
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    
    /**
     * Cursor ID to load graph data from API.
     */
    cursor_id: PropTypes.string,
    
    /**
     * Workspace ID for API context.
     */
    workspace_id: PropTypes.string,
    
    /**
     * FQL query to execute and visualize results.
     */
    query: PropTypes.string,

    /**
     * Natural language search text. When set, triggers ExplorerAPI.searchAsk()
     * which creates an A2A task, loads entities into the cursor, refreshes
     * the graph, and opens the task card.
     */
    search_text: PropTypes.string,

    /**
     * Pre-identified entities/keywords from autocomplete.
     * Used together with search_text for the searchAsk flow.
     * Array of { id, label, type } objects.
     */
    search_keywords: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string,
        label: PropTypes.string,
        type: PropTypes.string,
    })),

    /**
     * When true, create a new cursor for each search instead of reusing
     * the "me" cursor. Enables multiple concurrent/isolated search sessions.
     */
    create_cursor: PropTypes.bool,

    /**
     * Nonce value to force re-execution of a search even when search_text
     * hasn't changed. Set to Date.now() or a counter on each submit.
     */
    _search_nonce: PropTypes.number,

    /**
     * Array of node objects to render.
     */
    nodes: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        label: PropTypes.string,
        color: PropTypes.string,
        shape: PropTypes.string,
        size: PropTypes.number,
        title: PropTypes.string,
        group: PropTypes.string,
    })),
    
    /**
     * Array of edge objects to render.
     */
    edges: PropTypes.arrayOf(PropTypes.shape({
        from: PropTypes.string.isRequired,
        to: PropTypes.string.isRequired,
        label: PropTypes.string,
        color: PropTypes.string,
        width: PropTypes.number,
        arrows: PropTypes.string,
    })),
    
    /**
     * Graph configuration options.
     */
    options: PropTypes.shape({
        show_legend: PropTypes.bool,
        show_fql: PropTypes.bool,
        show_toolbar: PropTypes.bool,
        show_timeline: PropTypes.bool,
        show_chat: PropTypes.bool,
        node_color: PropTypes.string,
        theme: PropTypes.string,
        physics_enabled: PropTypes.bool,
        extension_mode: PropTypes.bool,
    }),
    
    /**
     * Array of currently selected node IDs.
     */
    selected_nodes: PropTypes.array,
    
    /**
     * Array of currently selected edge IDs.
     */
    selected_edges: PropTypes.array,
    
    /**
     * Height of the graph container.
     */
    height: PropTypes.string,
    
    /**
     * Width of the graph container.
     */
    width: PropTypes.string,
    
    /**
     * Base URL for API calls.
     */
    api_endpoint: PropTypes.string,
    
    /**
     * Additional CSS class names.
     */
    className: PropTypes.string,
    
    /**
     * Dash-assigned callback that should be called to report property changes
     * to Dash, to make them available for callbacks.
     */
    setProps: PropTypes.func,
};

// Named export for Dash compatibility
export { DashGraph };
